import { useCallback, useEffect, useState } from 'react'

import { ChatSession, useChat } from './gongChatApi'

export interface UseChatSessionsResult {
  sessions: ReadonlyArray<ChatSession>
  isLoading: boolean
  refresh: () => Promise<void>
  remove: (sessionId: string) => Promise<void>
}

export function useChatSessions(orgId: string | null): UseChatSessionsResult {
  const { listSessions, deleteSession } = useChat()
  const [sessions, setSessions] = useState<ReadonlyArray<ChatSession>>([])
  const [isLoading, setIsLoading] = useState(false)

  const refresh = useCallback(async () => {
    if (!orgId) {
      setSessions([])
      return
    }
    setIsLoading(true)
    try {
      const next = await listSessions(orgId)
      setSessions(next)
    } catch (error) {
      console.error('Failed to load chat sessions', error)
      setSessions([])
    } finally {
      setIsLoading(false)
    }
  }, [orgId, listSessions])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const remove = useCallback(
    async (sessionId: string) => {
      // Drop the row immediately so the UI repaints without waiting on the
      // network round-trips, then reconcile with the server.
      setSessions((prev) => prev.filter((s) => s.id !== sessionId))
      try {
        await deleteSession(sessionId)
        await refresh()
      } catch (error) {
        await refresh()
        throw error
      }
    },
    [deleteSession, refresh]
  )

  return { sessions, isLoading, refresh, remove }
}
